import axios from 'axios';
import {
  Setting,
  SettingHistory,
  SettingsByCategory,
  BulkSettingUpdate,
  SettingExportParams,
  SettingImport,
} from '../types/settings.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const settingsApi = axios.create({
  baseURL: `${API_URL}/settings`,
});

// Add auth token to requests
settingsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401
settingsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.config && !error.config._retry) {
      error.config._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          const { access } = response.data;
          localStorage.setItem('accessToken', access);
          error.config.headers.Authorization = `Bearer ${access}`;
          return settingsApi(error.config);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

class SettingsService {
  // Get all settings
  async getSettings(params?: {
    category?: string;
    search?: string;
    is_sensitive?: boolean;
    ui_group?: string;
  }): Promise<Setting[]> {
    const response = await settingsApi.get('/', { params });
    return response.data.results || response.data;
  }

  // Get settings grouped by category
  async getSettingsByCategory(): Promise<SettingsByCategory[]> {
    const response = await settingsApi.get('/by_category/');
    return response.data;
  }

  // Get a single setting
  async getSetting(id: string): Promise<Setting> {
    const response = await settingsApi.get(`/${id}/`);
    return response.data;
  }

  // Update a setting
  async updateSetting(id: string, value: any, changeReason?: string): Promise<Setting> {
    const response = await settingsApi.patch(`/${id}/`, { 
      value,
      change_reason: changeReason 
    });
    return response.data;
  }

  // Bulk update settings
  async bulkUpdateSettings(data: BulkSettingUpdate): Promise<{
    message: string;
    settings: Setting[];
  }> {
    const response = await settingsApi.post('/bulk_update/', data);
    return response.data;
  }

  // Reset settings to defaults
  async resetToDefaults(keys: string[]): Promise<{
    message: string;
    settings: Setting[];
  }> {
    const response = await settingsApi.post('/reset_to_defaults/', { keys });
    return response.data;
  }

  // Get setting history
  async getSettingHistory(id: string, params?: {
    page?: number;
    page_size?: number;
  }): Promise<{
    count: number;
    results: SettingHistory[];
  }> {
    const response = await settingsApi.get(`/${id}/history/`, { params });
    return response.data;
  }

  // Export settings
  async exportSettings(params?: SettingExportParams): Promise<Blob> {
    const response = await settingsApi.get('/export/', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // Import settings
  async importSettings(data: SettingImport): Promise<{
    message: string;
    imported: number;
    skipped: number;
    errors: string[];
  }> {
    const response = await settingsApi.post('/import_settings/', data);
    return response.data;
  }

  // Send a test email through the active mail path (Mailpit or real SMTP)
  async sendTestEmail(recipient?: string): Promise<{
    sent: boolean;
    test_mode: boolean;
    recipient: string;
  }> {
    const response = await settingsApi.post('/email/test/', recipient ? { recipient } : {});
    return response.data;
  }

  // Validate a setting value
  validateSettingValue(setting: Setting, value: any): string | null {
    switch (setting.data_type) {
      case 'INTEGER': {
        const intValue = parseInt(value);
        if (isNaN(intValue)) {
          return 'Value must be a valid integer';
        }
        if (setting.min_value !== undefined && intValue < setting.min_value) {
          return `Value must be at least ${setting.min_value}`;
        }
        if (setting.max_value !== undefined && intValue > setting.max_value) {
          return `Value must be at most ${setting.max_value}`;
        }
        break;
      }

      case 'DECIMAL': {
        const decimalValue = parseFloat(value);
        if (isNaN(decimalValue)) {
          return 'Value must be a valid number';
        }
        if (setting.min_value !== undefined && decimalValue < setting.min_value) {
          return `Value must be at least ${setting.min_value}`;
        }
        if (setting.max_value !== undefined && decimalValue > setting.max_value) {
          return `Value must be at most ${setting.max_value}`;
        }
        break;
      }

      case 'EMAIL': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      }

      case 'URL':
        try {
          new URL(value);
        } catch {
          return 'Please enter a valid URL';
        }
        break;

      case 'COLOR': {
        const colorRegex = /^#[0-9A-Fa-f]{6}$/;
        if (!colorRegex.test(value)) {
          return 'Color must be in hex format (e.g., #FF0000)';
        }
        break;
      }

      case 'JSON':
        try {
          JSON.parse(value);
        } catch {
          return 'Value must be valid JSON';
        }
        break;
    }

    // Check against choices if provided
    if (setting.choices && setting.choices.length > 0) {
      if (!setting.choices.includes(value)) {
        return `Value must be one of: ${setting.choices.join(', ')}`;
      }
    }

    // Check against validation regex if provided
    if (setting.validation_regex) {
      const regex = new RegExp(setting.validation_regex);
      if (!regex.test(value)) {
        return 'Value does not match the required format';
      }
    }

    return null;
  }
}

export default new SettingsService();